/**
 * Edit Profile Route Definition (Non-Lazy)
 *
 * Loader plus the route-level pending shell. The form, react-form and the
 * account section live in edit.lazy.tsx so their module imports stay out of
 * the entry chunk — which is exactly why the pendingComponent lives HERE: a
 * pending declared in the lazy file cannot render while that chunk itself is
 * still downloading.
 */
import { createFileRoute } from '@tanstack/react-router';
import { authenticatedGetShared } from '@/lib/api/authenticated-shared';
import { cacheFirst } from '@/lib/queries/cache-first';
import { FormPageTemplatePending } from '@/components/v2/page-template-pending';
import type { PrivateProfile } from '@/lib/api/v2/profile';

export const Route = createFileRoute('/_authenticated/profile/edit')({
  // Title inlined from the page's own "Edit Profile" heading (def files stay
  // eager and only import page-template-pending). The real page navigates back
  // with an arrow and saves from a header button (no inline submit bar), so
  // the pending mirrors that header family and omits the submit stub.
  pendingComponent: () => <FormPageTemplatePending title="Edit Profile" variant="arrow" />,
  loader: ({ context }) => {
    // ensureQueryData was the whole point of #1273's cache policy and this
    // route was the one file that bypassed it: a bare server-fn call fetched
    // on every hover AND again on the committed click, priming nothing the
    // form reads. A fresh ['profile'] — e.g. seconds after visiting /profile
    // — now resolves the loader without a network round trip.
    const refresh = () =>
      context.queryClient.ensureQueryData({
        queryKey: ['profile'],
        queryFn: () => authenticatedGetShared<PrivateProfile>('/v2/me/profile'),
      });
    // Cache-first gate (same ['profile'] key as the index route): a cached
    // entry resolves the loader instantly while the background refresh heals
    // staleness, so back-navigations never swap to the form skeleton; a cold
    // cache keeps the streamed fetch the Await boundary renders against.
    const { cached, stream } = cacheFirst(context.queryClient, ['profile'], refresh);
    return { profilePromise: cached !== undefined ? Promise.resolve(cached) : stream() };
  },
});
